import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchOrdersPeriods } from '../services/ordersDailyTruthClient';
import type { OrdersPeriod, OrdersPeriodGrain, OrdersPeriodsResponse } from '../types/ordersDailyTruth';

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError')
    || (err instanceof Error && err.name === 'AbortError')
  );
}

/**
 * Week / month / quarter / year trend figures, read from the stored period
 * records — the server never computes them on this request. Periods with no
 * figures come back in `unavailable` (a gap, never zero); when the server
 * requested a build it says when to ask again, and this hook does.
 *
 * Not available in demo mode (demo data has no stored periods).
 */
export function useOrdersPeriods(
  grain: OrdersPeriodGrain,
  from: string,
  to: string,
  opts?: { enabled?: boolean; demo?: boolean },
) {
  const enabled = opts?.enabled !== false && opts?.demo !== true;
  const [periods, setPeriods] = useState<OrdersPeriod[]>([]);
  const [unavailable, setUnavailable] = useState<OrdersPeriodsResponse['unavailable']>([]);
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const retryRef = useRef<number | null>(null);

  const load = useCallback(async (loadOpts?: { background?: boolean }) => {
    if (retryRef.current !== null) {
      window.clearTimeout(retryRef.current);
      retryRef.current = null;
    }
    abortRef.current?.abort();
    if (!enabled) {
      setPeriods([]);
      setUnavailable([]);
      setStale(false);
      setError(null);
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    abortRef.current = ac;
    if (!loadOpts?.background) setLoading(true);
    setError(null);
    try {
      const result = await fetchOrdersPeriods(grain, from, to, { signal: ac.signal });
      if (ac.signal.aborted) return;
      setPeriods(result.periods);
      setUnavailable(result.unavailable);
      setStale(result.stale);
      setLastLoadedAt(new Date().toISOString());
      setLoading(false);
      if (result.refresh_requested && result.retry_after_seconds) {
        retryRef.current = window.setTimeout(() => {
          void load({ background: true });
        }, result.retry_after_seconds * 1000);
      }
    } catch (err) {
      if (isAbortError(err) || ac.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load period figures');
      setLoading(false);
    }
  }, [enabled, grain, from, to]);

  useEffect(() => {
    void load();
    return () => {
      abortRef.current?.abort();
      if (retryRef.current !== null) window.clearTimeout(retryRef.current);
    };
  }, [load]);

  const refresh = useCallback((refreshOpts?: { background?: boolean }) => load(refreshOpts), [load]);

  return { periods, unavailable, stale, loading, error, lastLoadedAt, refresh, supported: opts?.demo !== true };
}
